import { IQueryContext } from 'src/app/services/query-context/models/IQueryContext';
import {
    DashboardItemComponentType,
    DashboardItemFactory,
    IDashboardItem,
    IDashboardItemFactoryOptions,
} from 'src/app/utils/dashboard-item';
import type {
    KpiTileIndicator,
    KpiTileTone,
    KpiTileViewModel,
} from './kpi-tiles.component';

/** How the x points of the loaded range are reduced into one headline value. */
export type KpiRangeAggregation =
    | 'sum'
    | 'mean'
    | 'min'
    | 'max'
    | 'first'
    | 'last';

/**
 * Declarative description of one tile — the KPI counterpart of the `xy_chart` options, so a tile
 * can be configured inline in the dashboard link instead of through a hand-written `composeData`.
 */
export interface KpiTileSpec {
    /** Tile caption. */
    label: string;
    /** Row column holding the value, e.g. `'transactions'`. */
    yColumnLabel?: string;
    /** Context-dependent column name (Net/Gross, currency, …). Takes precedence over `yColumnLabel`. */
    yColumnLabelGetter?: (context: IQueryContext) => string;
    /** Row column identifying one x point; rows sharing it (splits) are summed. Defaults to `'date'`. */
    xColumnLabel?: string;
    /** Sorts x points before range aggregation. Defaults to numeric order when both values are numbers, otherwise lexical order. */
    xValueSortFn?: (left: string, right: string) => number;
    /** Reduction over the x points. Defaults to `'sum'`. */
    rangeAggregation?: KpiRangeAggregation;
    /** Unit shown under the value. */
    unit?: string;
    /** Context-dependent unit; takes precedence over `unit`. */
    unitGetter?: (context: IQueryContext) => string;
    /** Tile accent colour. Defaults to neutral. */
    tone?: KpiTileTone;
    /** Head indicator, independent from the accent colour. */
    indicator?: KpiTileIndicator;
    /** Formats the headline value; defaults to an integer number. */
    valueFormatterGetter?: (
        context: IQueryContext
    ) => (value: number) => string;
    /** `'halves'` (default) compares the 2nd half of the range against the 1st; `'none'` hides the delta. */
    delta?: 'halves' | 'none';
    /** Caption after the delta. Defaults to `'vs 1st half'`. */
    deltaCaption?: string;
    /** Delta colour; by default derives from the delta sign. */
    deltaTone?: KpiTileTone;
    /** Formats the delta magnitude; defaults to a rounded percentage. */
    deltaFormatterGetter?: (
        context: IQueryContext
    ) => (delta: number) => string;
    /** HTML for the tile's documentation (info-circle) icon. */
    documentation?: string;
    /** Context-dependent documentation HTML; takes precedence over `documentation`. */
    documentationGetter?: (context: IQueryContext) => string;
}

/** Dashboard item that converts one data-loader result into the displayed tile models. */
export interface IKpiTilesDashboardItem extends IDashboardItem {
    composeData: (
        rawData: any[],
        context?: IQueryContext
    ) => KpiTileViewModel[];
}

/**
 * Factory options for one reusable KPI-tile dashboard item. Either configure the tiles
 * declaratively through `tileSpecs`, or take over the whole projection with `composeData`
 * (used when a state service already emits ready view models, e.g. Resource Flow).
 */
export interface KpiTilesDashboardItemFactoryOptions extends IDashboardItemFactoryOptions {
    createdInstanceId: string;
    /** Declarative specifications for tiles computed from the subscribed loader's rows. */
    tileSpecs?: KpiTileSpec[];
    /** Converts all values emitted by the subscribed data loader into view tiles. */
    composeData?: (
        rawData: any[],
        context?: IQueryContext
    ) => KpiTileViewModel[];
}

/** Factory for rendering KPI tiles through the standard dashboard-item/data-loader link. */
export class KpiTilesDashboardItemFactory extends DashboardItemFactory<
    IKpiTilesDashboardItem,
    KpiTilesDashboardItemFactoryOptions
> {
    override FACTORY_CLASS = 'KpiTilesDashboardItemFactory';
    override COMPONENT_CLASS: DashboardItemComponentType = 'KpiTilesComponent';

    override getInstance(
        options: KpiTilesDashboardItemFactoryOptions
    ): IKpiTilesDashboardItem {
        const tileSpecs = options.tileSpecs ?? [];
        return {
            instanceId: options.createdInstanceId,
            component: this.COMPONENT_CLASS,
            composeData:
                options.composeData ??
                ((rawData, context) =>
                    composeKpiTiles(tileSpecs, rawData, context)),
        };
    }
}

/** Projects the declarative specs onto the loader rows of the currently loaded range. */
function composeKpiTiles(
    tileSpecs: KpiTileSpec[],
    rawData: any[],
    context?: IQueryContext
): KpiTileViewModel[] {
    if (!context || !tileSpecs.length || !rawData?.length) {
        return [];
    }
    return tileSpecs.map((tileSpec) =>
        composeKpiTile(tileSpec, rawData, context)
    );
}

function composeKpiTile(
    tileSpec: KpiTileSpec,
    rawData: any[],
    context: IQueryContext
): KpiTileViewModel {
    const valueKey = tileSpec.yColumnLabelGetter
        ? tileSpec.yColumnLabelGetter(context)
        : (tileSpec.yColumnLabel ?? '');
    const xKey = tileSpec.xColumnLabel ?? 'date';
    const aggregation = tileSpec.rangeAggregation ?? 'sum';

    // Rows are (split × x point) — fold splits together so every x point contributes once.
    const perX: Record<string, number> = {};
    for (const row of rawData) {
        const x = String(row[xKey] ?? '');
        perX[x] = (perX[x] || 0) + (+row[valueKey] || 0);
    }
    const points = Object.keys(perX).sort(
        tileSpec.xValueSortFn ?? defaultKpiXValueSortFn
    );
    const reduce = (range: string[]) =>
        aggregateKpiRange(
            range.map((x) => perX[x]),
            aggregation
        );

    const valueFormatter = tileSpec.valueFormatterGetter?.(context);
    const deltaFormatter = tileSpec.deltaFormatterGetter?.(context);

    return {
        label: tileSpec.label,
        value: reduce(points),
        unit: tileSpec.unitGetter
            ? tileSpec.unitGetter(context)
            : tileSpec.unit,
        tone: tileSpec.tone,
        indicator: tileSpec.indicator,
        valueFormatter,
        delta:
            tileSpec.delta === 'none'
                ? null
                : computeKpiHalvesDelta(points, reduce),
        deltaCaption: tileSpec.deltaCaption ?? 'vs 1st half',
        deltaTone: tileSpec.deltaTone,
        deltaFormatter,
        documentation: tileSpec.documentationGetter
            ? tileSpec.documentationGetter(context)
            : tileSpec.documentation,
    };
}

function defaultKpiXValueSortFn(left: string, right: string): number {
    const leftNumber = Number(left);
    const rightNumber = Number(right);
    if (Number.isFinite(leftNumber) && Number.isFinite(rightNumber)) {
        return leftNumber - rightNumber;
    }
    return left.localeCompare(right);
}

function aggregateKpiRange(
    values: number[],
    aggregation: KpiRangeAggregation
): number {
    if (!values.length) {
        return 0;
    }
    switch (aggregation) {
        case 'mean':
            return values.reduce((a, b) => a + b, 0) / values.length;
        case 'min':
            return Math.min(...values);
        case 'max':
            return Math.max(...values);
        case 'first':
            return values[0];
        case 'last':
            return values[values.length - 1];
        default:
            return values.reduce((a, b) => a + b, 0);
    }
}

/** Second half of the range against the first — null when there is nothing to compare against. */
function computeKpiHalvesDelta(
    points: string[],
    reduce: (range: string[]) => number
): number | null {
    const half = Math.floor(points.length / 2);
    if (half === 0) {
        return null;
    }
    const first = reduce(points.slice(0, half));
    const second = reduce(points.slice(points.length - half));
    if (first === 0) {
        return null;
    }
    return (second - first) / Math.abs(first);
}
